import React, {useCallback, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import styles from './UserGrid.module.scss';
import gridStyles from '../../../../../GridStyles.module.scss';
import {bindActionCreators, Dispatch} from 'redux';
import {ConfirmationDialog} from '../../../../../../components/util/ConfirmationDialog/ConfirmationDialog';
import IconButton, {
  LabeledIconButton,
  LabeledIconButtonProps
} from '../../../../../../components/util/widgets/IconButton/IconButton';
import {Col, Modal, Row, Button as BootstrapButton} from 'react-bootstrap';
import { Paper, Avatar, Badge, Button } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import StyledMantineTable, { type MRT_ColumnDef } from '../../../../../../components/util/StyledMantineTable/StyledMantineTable';
import {AccountStatus, User, userStore} from '../../../../../../redux/entities/user';
import UserModal from '../UserModal/UserModal';
import {makeUser} from '../../../../../../redux/factory/user';
import {Navigate, useSearchParams} from 'react-router-dom';
import {Role, roleStore} from '../../../../../../redux/entities/role';
import {RoutePaths} from '../../../../../../router/RoutePaths';
import {format, utcToZonedTime} from 'date-fns-tz';
import {parseISO} from 'date-fns';
import {AppTheme, noProfilePicture} from '../../../../../../appTheme';
import {CommonState} from '../../../../../../redux';
import {dateTimeFormatter, localTz, localTzFriendlyFormat} from '../../../../../../util/date';
import {StyledLabeledIconButton} from "../../../../../../components/util/widgets/IconButton/IconButton";
// ... existing code ...

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;



type modalType =
  | 'none'
  | 'editUser'
  | 'selectUser'
  | 'createAdministrator'
  | 'createVolunteer'
  | 'createTeamMember'
  | 'createWarehouse';

function UserGrid(props: Props) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const {archivedUsers, nonArchivedUsers, roles, getRoleByName, currentUser,
    actions: {archiveUser, restoreUser, upsertUser}} = props;
  const [isDeleting, setIsDeleting] = useState('');
  const [isActivating, setIsActivating] = useState('');
  const [viewingArchived, setViewingArchived] = useState(false);
  const [currentModalType, setCurrentModalType] = useState<modalType>('none');
  const [editingUser, setEditingUser] = useState(makeUser());
  const [searchParams] = useSearchParams();
  const [redirectUrl, setRedirectUrl] = useState('');
  const resetState = () => {
    setEditingUser(makeUser());
    setCurrentModalType('none');
  };

  const getRoleForUserModal = (
    userType:
      | 'editUser'
      | 'createAdministrator'
      | 'createVolunteer'
      | 'createTeamMember'
      | 'createWarehouse'
  ): Role | undefined => {
    if (userType === 'createAdministrator') return getRoleByName('Administrator');
    if (userType === 'createVolunteer') return getRoleByName('Volunteer');
    if (userType === 'createTeamMember') return getRoleByName('Team Member');
    if (userType === 'createWarehouse') return getRoleByName('Warehouse');
  };

  const renderRedirect = () => {
    if (redirectUrl.length !== 0) {
      return <Navigate to={redirectUrl} />;
    }
    return null;
  };

  const userCreationTypeSelection = () => (
    <Modal show={true} centered={true} size={'sm'} className={styles['on-top']} onHide={() => undefined}>
      <Modal.Body>
        <Modal.Title>
          Create a New User
        </Modal.Title>
        <Row style={{paddingTop: '20px', justifyContent: 'center', alignItems: 'center', gap: '12px'}}>
          <StyledLabeledIconButton
            label={'Administrator'}
            icon={'user-lock'}
            size={'2x'}
            onClick={() => {
              setEditingUser(makeUser());
              setCurrentModalType('createAdministrator');
            }}
          />
          <StyledLabeledIconButton
            label={'Volunteer'}
            icon={'hands-helping'}
            size={'2x'}
            onClick={() => {
              setEditingUser(makeUser());
              setCurrentModalType('createVolunteer');
            }}
          />
          <StyledLabeledIconButton
            label={'Team Member'}
            icon={'users'}
            size={'2x'}
            onClick={() => {
              setEditingUser(makeUser());
              setCurrentModalType('createTeamMember');
            }}
          />
          <StyledLabeledIconButton
            label={'Warehouse'}
            icon={'warehouse'}
            size={'2x'}
            onClick={() => {
              setEditingUser(makeUser());
              setCurrentModalType('createWarehouse');
            }}
          />
        </Row>
        <Row style={{paddingTop: '10px'}}>
          <Button color="red" variant="light" className={styles['modal-buttons']} onClick={() => setCurrentModalType('none')}>
            Cancel
          </Button>
        </Row>
      </Modal.Body>
    </Modal>
  );

  const renderCurrentModal = () => {
    if (currentModalType === 'none')
      return null;
    if (currentModalType === 'selectUser')
      return userCreationTypeSelection();
    return (
      <UserModal
        editable={true}
        onSubmit={resetState}
        onCancel={resetState}
        role={getRoleForUserModal(currentModalType)}
        existingUser={editingUser?.id ? editingUser : undefined}
      />
    );
  };

  const actionsFormatter = useCallback((cell: any, user: User) => {
    return (
      <div className={gridStyles['table-actions-wrapper']}>
        {user.archivedAt ?
            <IconButton
              icon={'plus'}
              styles={{marginLeft: '.5rem'}}
              size={'1x'}
              iconToolTipText={'Activate User'}
              onClick={() => setIsActivating(user.id.toString())}
              color={'green'}
            />
        : (
          <>
            <div>
              <IconButton
                icon='edit'
                size={'1x'}
                styles={{marginLeft: '.5rem'}}
                onClick={() => {
                  if(user.id === currentUser?.id) {
                    setRedirectUrl(RoutePaths.profilePaths.Edit);
                  } else {
                    setEditingUser(user);
                    setCurrentModalType('editUser');
                  }
                }}
                color={'#005A9C'}
              />
            </div>
            {user.id !== currentUser?.id && <div>
              <IconButton
                icon={'trash-alt'}
                styles={{color: AppTheme.colors.danger, marginLeft: '.5rem'}}
                size={'1x'}
                iconToolTipText={'Deactivate User'}
                onClick={() => setIsDeleting(user.id.toString())}
              />
            </div>}
          </>
          )
        }
      </div>
    );
  }, [viewingArchived]);

  const profilePictureFormatter = (cell: any, user: User) => {
    return (
      <Avatar
        src={user.profilePicturePath !== '' ? `${user.profilePicturePath}` : noProfilePicture()}
        alt={'Profile picture'}
        radius="xl"
        size={"sm"}
        style={{objectFit: 'contain'}}
      />
    );
  };

  const accountStatusFormatter = (cell: any, user: User) => {
    const statusLabel = AccountStatus[user.accountStatus].replace(/([A-Z])/g, ' $1').trim();
    const statusColor: string = (() => {
      switch (user.accountStatus) {
        case AccountStatus.Active:
          return 'green';
        case AccountStatus.PendingApproval:
          return 'yellow';
        case AccountStatus.EmailValidationRequired:
          return 'yellow';
        default:
          return 'gray';
      }
    })();
    return (
      <Badge color={statusColor} variant="light" radius="sm">
        {statusLabel}
      </Badge>
    );
  };

  const filterActivatingUser = () => {
    return nonArchivedUsers.filter(u => u.email === searchParams.get('edit')!);
  };

  const data = useMemo(() => (viewingArchived ? archivedUsers : nonArchivedUsers), [viewingArchived, archivedUsers, nonArchivedUsers]);

  const columns: MRT_ColumnDef<User>[] = useMemo(() => [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'id',
      enableHiding: true,
      enableSorting: true,
    },
    {
      id: 'profilePicturePath',
      header: 'Picture',
      accessorKey: 'profilePicturePath',
      enableSorting: false,
      Cell: ({ row }) => profilePictureFormatter(null, row.original as User),
      size: 56,
      minSize: 40,
      maxSize: 64,
      enableResizing: false,
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      enableSorting: true,
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
      enableSorting: true,
    },
    {
      id: 'role',
      header: 'Role',
      accessorFn: (row) => row.role.roleName,
      enableSorting: true,
    },
    {
      id: 'createdAt',
      header: `Created On (${localTzFriendlyFormat})`,
      accessorFn: (row) => dateTimeFormatter(row.createdAt),
      enableHiding: true,
    },
    {
      id: 'archivedAt',
      header: `Deactivated On (${localTzFriendlyFormat})`,
      accessorFn: (row) => row.archivedAt ? dateTimeFormatter(row.archivedAt) : '',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessorFn: () => '',
      Cell: ({ row }) => actionsFormatter(null, row.original as User),
      size: 0,
      minSize: 0,
      maxSize: 0,
    },
  ], [viewingArchived]);

  return (
    <>
      {renderRedirect()}
      <Paper withBorder shadow="sm" radius="md" p="sm" className={gridStyles['responsive-table-wrapper']}>
        <StyledMantineTable
          columns={columns}
          data={data}
          initialState={isMobile ? { columnVisibility: { profilePicturePath: false, id: false } } : { columnVisibility: { profilePicturePath: false, id: false }, pagination: {pageSize: 15} }}
          topToolbarActions={
            <div className={gridStyles['button-group']}>
              <Button
                onClick={() => setCurrentModalType('selectUser')}
                style={{ backgroundColor: AppTheme.colors.themePurple }}
              >
                Add User
              </Button>
              <Button
                style={{ marginLeft: 'auto', marginRight: '.5rem', marginBottom: 'auto', marginTop: 'auto', backgroundColor: AppTheme.colors.themePurple  }}
                onClick={() => setViewingArchived(v => !v)}
                aria-label={viewingArchived ? 'View Active Users' : 'View Archived Users'}
              >
                {viewingArchived ? 'View Active Users' : 'View Archived Users'}
              </Button>
            </div>
          }
          mantineTableProps={{
            striped: true,
            highlightOnHover: true,
            withColumnBorders: true,
            className: gridStyles['table-auto'],
          }}
        />
      </Paper>

      {isDeleting !== '' && (
        <ConfirmationDialog
          onAccept={async () => {
            await archiveUser(isDeleting);
            setIsDeleting('');
          }}
          onDecline={async () => { setIsDeleting(''); }}
          open={isDeleting !== ''}
          prompt='Are you sure you want to archive this User?'
          positiveText='Yes'
          negativeText='No'
          positiveVariant='success'
          negativeVariant='danger'
        />
      )}
      {isActivating !== '' && (
        <ConfirmationDialog
          onAccept={async () => {
            await restoreUser(isActivating);
            setIsActivating('');
          }}
          onDecline={async () => { setIsActivating(''); }}
          open={isActivating !== ''}
          prompt='Are you sure you want to Activate this User?'
          positiveText='Yes'
          negativeText='No'
          positiveVariant='success'
          negativeVariant='danger'
        />
      )}
      {renderCurrentModal()}
    </>
  );

}

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    archiveUser: userStore.actions.archiveUser,
    restoreUser: userStore.actions.restoreUser,
    upsertUser: userStore.actions.upsert
  }, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  nonArchivedUsers: userStore.selectors.getNonArchivedUsers(state),
  archivedUsers: userStore.selectors.getArchivedUsers(state),
  getUserById: userStore.selectors.getById(state),
  roles: roleStore.selectors.getAsArray(state),
  getRoleByName: roleStore.selectors.getRoleByName(state),
  currentUser: userStore.selectors.getCurrentUser(state)
});
export default connect(mapStateToProps, mapDispatchToProps)(UserGrid);
